const BASE = "/api";
let authToken = "";

export function setAuthToken(token = "") {
  authToken = String(token || "").trim();
}

function fetch(input, options = {}) {
  return globalThis.fetch(input, {
    ...options,
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(options.headers || {}),
    },
  });
}

const handle = async (res) => {
  const data = await res.json();
  if (!res.ok) {
    const error = new Error(data.message || "İstek başarısız");
    error.status = res.status;
    error.code = data.code;
    error.usage = data.usage;
    error.data = data;
    throw error;
  }
  return data;
};

export const api = {
  getMeals: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetch(`${BASE}/meals${q ? `?${q}` : ""}`).then(handle);
  },
  getMeal: (id, lang = "tr") => fetch(`${BASE}/meals/${id}?lang=${lang}`).then(handle),
  getCuisines: () => fetch(`${BASE}/meals/cuisines`).then(handle),
  getIngredients: (search = "") =>
    fetch(`${BASE}/meals/ingredients?search=${encodeURIComponent(search)}`).then(handle),
  getDessertTypes: () => fetch(`${BASE}/meals/dessert-types`).then(handle),
  getDailyPlan: ({ category = "all", cuisine = "all", refresh = false, lang = "tr", userId = "", guestId = "", isPremium = false } = {}) =>
    fetch(`${BASE}/meals/daily-plan?${new URLSearchParams({ category, cuisine, refresh, lang, userId, guestId, isPremium }).toString()}`).then(handle),
  suggestByIngredients: (payload) =>
    fetch(`${BASE}/meals/suggest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  suggestByIngredientsAi: (payload) =>
    fetch(`${BASE}/meals/suggest/ai`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  createRecipe: (payload) =>
    fetch(`${BASE}/meals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  uploadRecipeImage: (file, userId) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("userId", userId);
    return fetch(`${BASE}/meals/upload-image`, {
      method: "POST",
      body: formData,
    }).then(handle);
  },
  rateRecipe: (recipeId, payload) =>
    fetch(`${BASE}/meals/${recipeId}/rating`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  reportCopyright: (recipeId, payload) =>
    fetch(`${BASE}/meals/${recipeId}/copyright-report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  getMealConsumption: (recipeId, userId) =>
    fetch(`${BASE}/meals/${recipeId}/consumption?${new URLSearchParams({ userId }).toString()}`).then(handle),
  markMealConsumed: (recipeId, payload) =>
    fetch(`${BASE}/meals/${recipeId}/consumption`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  unmarkMealConsumed: (recipeId, userId) =>
    fetch(`${BASE}/meals/${recipeId}/consumption?${new URLSearchParams({ userId }).toString()}`, { method: "DELETE" }).then(handle),

  getShoppingList: (profile = {}) => fetch(`${BASE}/shopping?${new URLSearchParams(profile).toString()}`).then(handle),
  addToShoppingList: (name, profile = {}) =>
    fetch(`${BASE}/shopping`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, ...profile }),
    }).then(handle),
  toggleShoppingItem: (id, profile = {}) =>
    fetch(`${BASE}/shopping/${id}/toggle`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    }).then(handle),
  removeShoppingItem: (id, profile = {}) =>
    fetch(`${BASE}/shopping/${id}?${new URLSearchParams(profile).toString()}`, { method: "DELETE" }).then(handle),
  clearCheckedItems: (profile = {}) =>
    fetch(`${BASE}/shopping/clear/checked?${new URLSearchParams(profile).toString()}`, { method: "DELETE" }).then(handle),

  getFavorites: (profile = {}) => fetch(`${BASE}/favorites?${new URLSearchParams(profile).toString()}`).then(handle),
  toggleFavorite: (recipeId, profile = {}) =>
    fetch(`${BASE}/favorites/${recipeId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    }).then(handle),

  getDietitianAdvice: (lang = "tr", profile = {}) =>
    fetch(`${BASE}/dietitian/daily?${new URLSearchParams({ lang, ...profile }).toString()}`).then(handle),
  askDietitian: (question, lang = "tr", profile = {}) =>
    fetch(`${BASE}/dietitian/ask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question, lang, ...profile }),
    }).then(handle),
  getUsageStatus: (usageType, profile = {}) =>
    fetch(`${BASE}/usage/status/${usageType}?${new URLSearchParams(profile).toString()}`).then(handle),
  consumeUsage: (usageType, profile = {}) =>
    fetch(`${BASE}/usage/consume`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ usageType, ...profile }),
    }).then(handle),
  getSettings: () => fetch(`${BASE}/settings`).then(handle),
  getGoogleClientId: () => fetch(`${BASE}/auth/google-client-id`).then(handle),
  searchNutrition: (query, { refresh = false, limit = 6 } = {}) =>
    fetch(`${BASE}/nutrition/search?${new URLSearchParams({ q: query, refresh, limit }).toString()}`).then(handle),
  getNutritionReferenceValues: () => fetch(`${BASE}/nutrition/reference-values`).then(handle),
  estimateMealNutrition: (ingredients, profile = {}) =>
    fetch(`${BASE}/nutrition/meal-estimate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ingredients, ...profile }),
    }).then(handle),
  estimatePhotoNutrition: (file, profile = {}) => {
    const formData = new FormData();
    formData.append("image", file);
    Object.entries(profile).forEach(([key, value]) => formData.append(key, value));
    return fetch(`${BASE}/nutrition/photo-estimate`, {
      method: "POST",
      body: formData,
    }).then(handle);
  },
  updateAdminSettings: (payload) =>
    fetch(`${BASE}/admin/settings`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  getAdminRecipes: (userId, status = "approved") =>
    fetch(`${BASE}/admin/recipes?${new URLSearchParams({ userId, status }).toString()}`).then(handle),
  getAdminTestUsers: (userId, filter = "all") =>
    fetch(`${BASE}/admin/test-users?${new URLSearchParams({ userId, filter }).toString()}`).then(handle),
  getAdminCopyrightReports: (userId, status = "new") =>
    fetch(`${BASE}/admin/copyright-reports?${new URLSearchParams({ userId, status }).toString()}`).then(handle),
  importThemealdbRecipes: (payload) =>
    fetch(`${BASE}/admin/themealdb/import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  updateAdminCopyrightReport: (reportId, payload) =>
    fetch(`${BASE}/admin/copyright-reports/${reportId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  moderateRecipe: (recipeId, payload) =>
    fetch(`${BASE}/admin/recipes/${recipeId}/moderation`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  updateAdminRecipeContent: (recipeId, payload) =>
    fetch(`${BASE}/admin/recipes/${recipeId}/content`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  updateAdminRecipeMedia: (recipeId, payload) =>
    fetch(`${BASE}/admin/recipes/${recipeId}/media`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  uploadAdminRecipeImage: (recipeId, file, userId) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("userId", userId);
    return fetch(`${BASE}/admin/recipes/${recipeId}/image?${new URLSearchParams({ userId }).toString()}`, {
      method: "POST",
      body: formData,
    }).then(handle);
  },
  socialLogin: (payload) =>
    fetch(`${BASE}/auth/social`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  adminCodeLogin: (payload) =>
    globalThis.fetch(`${BASE}/auth/admin-code?${new URLSearchParams({
      email: payload?.email || "",
      code: payload?.code || "",
    }).toString()}`, {
      method: "POST",
      cache: "no-store",
    }).then(handle),
  getUser: (userId) => fetch(`${BASE}/auth/me/${userId}`).then(handle),
  updateUserProfile: (userId, payload) =>
    fetch(`${BASE}/auth/me/${userId}/profile`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  getWeeklyProfile: (userId) => fetch(`${BASE}/auth/me/${userId}/weekly-profile`).then(handle),
  saveWeeklyProfile: (userId, payload) =>
    fetch(`${BASE}/auth/me/${userId}/weekly-profile`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  getWeeklyProfileReview: (userId, payload = {}) =>
    fetch(`${BASE}/auth/me/${userId}/weekly-review`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
  recordActivity: () =>
    fetch(`${BASE}/auth/activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }).then(handle),
};
